import { AlertTriangle, CalendarPlus } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button, ModalContentWrapper } from '@/components/ui';
import { useExtendLoan } from '@/hooks/use-extensions';
import type { Loan } from '@/types';

interface Props {
  loan: Loan;
  open: boolean;
  onClose: () => void;
}

export function ExtensionModal({ loan, open, onClose }: Props) {
  const [additionalMonths, setAdditionalMonths] = useState(3);
  const [extensionFee, setExtensionFee] = useState('');
  const [extensionFeeRetained, setExtensionFeeRetained] = useState('');
  const [extensionFeePaidOut, setExtensionFeePaidOut] = useState('');
  const [newInterestRate, setNewInterestRate] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const extendLoan = useExtendLoan(loan.id);

  if (!open) return null;

  const currentMaturity = loan.maturityDate ? new Date(loan.maturityDate) : null;
  const newMaturity = currentMaturity
    ? new Date(new Date(currentMaturity).setMonth(currentMaturity.getMonth() + additionalMonths))
    : null;

  const newTermMonths = loan.termMonths + additionalMonths;
  const extensionCount = (loan.extensions?.length ?? 0) + 1;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const input: any = {
      additionalMonths,
    };

    if (extensionFee) input.extensionFee = parseFloat(extensionFee);
    if (extensionFeeRetained) input.extensionFeeRetained = parseFloat(extensionFeeRetained);
    if (extensionFeePaidOut) input.extensionFeePaidOut = parseFloat(extensionFeePaidOut);
    if (newInterestRate) input.newInterestRate = parseFloat(newInterestRate);
    if (reason) input.reason = reason;
    if (notes) input.notes = notes;

    extendLoan.mutate(input, {
      onSuccess: () => {
        onClose();
        // Reset form
        setAdditionalMonths(3);
        setExtensionFee('');
        setExtensionFeeRetained('');
        setExtensionFeePaidOut('');
        setNewInterestRate('');
        setReason('');
        setNotes('');
      },
    });
  }

  // Auto-calculate retained fees (default: the lender retains the full fee).
  function handleExtensionFeeChange(val: string) {
    setExtensionFee(val);
    if (val && !extensionFeeRetained) {
      setExtensionFeeRetained(val);
    }
  }

  function fmtDate(d: Date | null) {
    if (!d) return '—';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function fmtCurrency(v: string) {
    const n = parseFloat(v);
    if (isNaN(n)) return v;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(n);
  }

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <CalendarPlus className="w-4 h-4 text-blue-600" />
          <span>Extend Loan Term</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            Extension #{extensionCount}
          </span>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Current terms summary */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 text-sm">
          <p className="font-medium text-gray-700 mb-1.5">Current Terms — {loan.loanNumber}</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Term</span>
              <p className="font-medium text-gray-900">{loan.termMonths} months</p>
            </div>
            <div>
              <span className="text-gray-500">Maturity</span>
              <p className="font-medium text-gray-900">
                {currentMaturity ? fmtDate(currentMaturity) : '—'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Rate</span>
              <p className="font-medium text-gray-900">
                {parseFloat(loan.interestRate).toFixed(2)}%
              </p>
            </div>
            <div>
              <span className="text-gray-500">Principal</span>
              <p className="font-medium text-gray-900">{fmtCurrency(loan.principal)}</p>
            </div>
            <div>
              <span className="text-gray-500">Status</span>
              <p className="font-medium text-gray-900">{loan.status.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <span className="text-gray-500">Previous Extensions</span>
              <p className="font-medium text-gray-900">{loan.extensions?.length ?? 0}</p>
            </div>
          </div>
        </div>

        {/* Extension months */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Months <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={60}
              value={additionalMonths}
              onChange={e => setAdditionalMonths(parseInt(e.target.value) || 1)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <div className="text-xs text-gray-500">
              <span className="text-gray-900 font-medium">{loan.termMonths}</span> months →{' '}
              <span className="text-blue-700 font-semibold">{newTermMonths}</span> months
            </div>
          </div>
          {newMaturity && (
            <p className="text-xs text-gray-500 mt-1">
              New maturity:{' '}
              <span className="text-blue-700 font-medium">{fmtDate(newMaturity)}</span>
            </p>
          )}
        </div>

        {/* Extension fee */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Extension Fee</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Total Fee</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={extensionFee}
                onChange={e => handleExtensionFeeChange(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Lender Retains</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={extensionFeeRetained}
                onChange={e => setExtensionFeeRetained(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Paid Out</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={extensionFeePaidOut}
                onChange={e => setExtensionFeePaidOut(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Interest rate change (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Interest Rate{' '}
            <span className="text-xs text-gray-400">(optional — leave blank to keep current)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={newInterestRate}
              onChange={e => setNewInterestRate(e.target.value)}
              placeholder={parseFloat(loan.interestRate).toFixed(2)}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-sm text-gray-500">% p.a.</span>
            {newInterestRate && parseFloat(newInterestRate) !== parseFloat(loan.interestRate) && (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Rate changing from {parseFloat(loan.interestRate).toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Extension
          </label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Borrower requested additional time, project delays"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Internal notes about this extension..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <button
            type="submit"
            disabled={extendLoan.isPending || additionalMonths < 1}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {extendLoan.isPending ? (
              <>
                <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
                Extending…
              </>
            ) : (
              <>
                <CalendarPlus className="w-3.5 h-3.5" />
                Extend Loan
              </>
            )}
          </button>
        </div>

        {extendLoan.isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
            {(extendLoan.error as any)?.response?.status === 429
              ? 'Too many requests — please wait a moment then try again'
              : (extendLoan.error as any)?.response?.data?.message ||
                'Failed to extend loan. Please try again.'}
          </div>
        )}
      </form>
    </ModalContentWrapper>
  );
}
